import { useEffect } from 'react';
import { useFarmStore } from '@/stores/farmStore';
import { useFarms } from './useFarms';

/**
 * The farm the app is acting on. The persisted `selectedFarmId` is only honored
 * while it is one of the user's farms (a stale id from another account, or a
 * deleted farm, falls back to the first farm); null while the farm list is
 * unknown or empty.
 */
export function useCurrentFarm() {
  const { selectedFarmId, setSelectedFarmId } = useFarmStore();
  const { farms, isSettled } = useFarms();

  const selectionIsValid = !!selectedFarmId && farms.some((f) => f.id === selectedFarmId);
  const farmId = !isSettled ? null : selectionIsValid ? selectedFarmId : farms[0]?.id ?? null;
  const currentFarm = farms.find((f) => f.id === farmId) ?? null;

  return {
    farmId,
    currentFarm,
    farms,
    isLoading: !isSettled,
    setSelectedFarmId,
  };
}

/**
 * Persists the resolved farm back to the store. Mounted once in the app layout;
 * every consumer already reads the resolved id from useCurrentFarm.
 */
export function useReconcileFarmSelection() {
  const { selectedFarmId, setSelectedFarmId } = useFarmStore();
  const { farms, isSuccess } = useFarms();

  useEffect(() => {
    if (!isSuccess) return;
    const next = farms.some((f) => f.id === selectedFarmId) ? selectedFarmId : farms[0]?.id ?? null;
    if (next !== selectedFarmId) setSelectedFarmId(next);
  }, [isSuccess, farms, selectedFarmId, setSelectedFarmId]);
}
